/**
 * ShellProvider: tab state, URL sync (spec §4), published chat contexts and
 * registered runs for the UI shell.
 *
 * URL sync rules, each implemented once:
 *  - the address bar equals the ACTIVE tab's path;
 *  - in-tab navigation of the active tab pushes (replaces for REPLACE/POP);
 *  - focus/open/close use replace; an inactive tab never touches history;
 *  - an outer change the shell did not cause drives the shell (`selfNav`
 *    marks the ones it did, so nothing replays).
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { DEFAULT_ANCHOR_LENGTH } from '../types/qa';
import type { SimpleCitation } from '../types/qa';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import { buildChatScope, readShell, writeShellTabs } from '../utils/chatHistoryStore';
import { setShellOpener } from '../utils/openInNewTab';
import { safeCompetitiveIntelUrl } from '../utils/competitiveIntelUrl';
import {
  citationOnTabPage, citationTargetPath, pathnameOf, tabShowsFdaLetter,
} from './citationTarget';
import { isShellPath, normalizeShellPath } from './shellRoutes';
import { ShellContext } from './shellContext';
import { nextRunSeq } from './runResults';
import type { RunResult } from './runResults';
import {
  canonicalTabPath, closeTab as closeTabState, confirmOpen, focusTab as focusTabState, isHomePath,
  moveTab as moveTabState, navigateTab, requestOpen, retitleTab, titleForPath,
} from './tabState';
import type { PendingOpen, TabsState } from './tabState';
import type {
  CitationVia, PublishedContext, PublishedHandlers, ReportedRun, ShellApi,
} from './types';

const RELEVANCE_SCORE_KEY = 'labelogy-show-relevance-score';

function currentScope(): string | null {
  return buildChatScope(auth.currentUser?.uid, getImpersonationHeaders()['X-Impersonate-Org']);
}

function readRelevanceScore(): boolean {
  try {
    return localStorage.getItem(RELEVANCE_SCORE_KEY) === 'true';
  } catch {
    return false;
  }
}

type Navigator = (path: string, opts?: { replace?: boolean }) => void;

/**
 * The destination page's handler for a chat click, by how it was clicked: a
 * chunk click and an inline section link keep their own page handlers (sentence
 * highlight, SBA section resolution); either falls back to the citation handler.
 */
function citationHandler(
  h: PublishedHandlers | undefined, c: SimpleCitation, via: CitationVia,
): (() => void) | null {
  if (!h) return null;
  if (via === 'chunk' && h.onChunkClick) {
    const onChunk = h.onChunkClick;
    return () => onChunk({
      chunkId: c.chunk_id ?? null,
      sectionNumber: c.section_number ?? '',
      anchorStart: c.anchor_start ?? '',
      anchorEnd: c.anchor_end ?? '',
      anchorLength: c.anchor_length ?? DEFAULT_ANCHOR_LENGTH,
    });
  }
  if (via === 'section' && h.onSectionLinkClick) {
    const onSection = h.onSectionLinkClick;
    return () => onSection(c.section_number ?? '', c.drug_id ?? undefined);
  }
  const onCitation = h.onCitationClick;
  return onCitation ? () => onCitation(c) : null;
}

/** Restore persisted tabs, then apply the cold deep link (spec §4). */
function initialShell(outerPath: string, scope: string | null): { state: TabsState; pending: PendingOpen | null } {
  const snap = readShell(scope);
  // A persisted tab whose path is no longer a shell path (an older build's
  // bad write) would throw on every reload's address-bar sync: drop it.
  const filtered = (snap?.tabs ?? []).filter((t) => isShellPath(t.path));
  // R1.2a(b): a persisted alias tab (see canonicalTabPath) is `/account`; keep only the first such tab.
  let seenAccount = false;
  const tabs = filtered.flatMap((t) => {
    if (canonicalTabPath(t.path) !== '/account') return [t];
    if (seenAccount) return [];
    seenAccount = true;
    return [{ ...t, path: '/account', title: titleForPath('/account') }];
  });
  const persistedActive = tabs.some((t) => t.id === snap?.activeTabId) ? snap!.activeTabId : null;
  let state: TabsState = { tabs, activeTabId: persistedActive ?? tabs[tabs.length - 1]?.id ?? null };
  if (isHomePath(outerPath) || !isShellPath(outerPath)) return { state, pending: null };
  const r = requestOpen(state, outerPath, Date.now());
  if (r.kind === 'needsEviction') return { state, pending: r.pending };
  state = r.state;
  return { state, pending: null };
}

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const outerPath = location.pathname + location.search;

  // Captured once: a mid-session identity change must never file one
  // identity's tabs under another's key (D6/D8).
  const scopeRef = useRef<string | null | undefined>(undefined);
  if (scopeRef.current === undefined) scopeRef.current = currentScope();
  const init = useRef<ReturnType<typeof initialShell> | null>(null);
  if (!init.current) init.current = initialShell(outerPath, scopeRef.current);

  const [state, setState] = useState<TabsState>(init.current.state);
  const stateRef = useRef(state);
  const [pending, setPending] = useState<PendingOpen | null>(init.current.pending);
  const [announcement, setAnnouncement] = useState<{ path: string; body: string } | null>(null);
  const [contexts, setContexts] = useState<Record<string, PublishedContext>>({});
  const [runs, setRuns] = useState<Record<string, RunResult>>({});
  const [showRelevanceScore, setShowRelevanceScoreState] = useState<boolean>(readRelevanceScore);
  const handlersRef = useRef<Record<string, PublishedHandlers>>({});
  const navigators = useRef<Map<string, Navigator>>(new Map());
  // A citation waiting for its destination tab's page to publish handlers.
  // Keyed by tab; `pathname` is the page it was meant for (never delivered to another).
  const queuedCitations = useRef<Record<string, { c: SimpleCitation; via: CitationVia; pathname: string }>>({});
  // Same, for a destination whose tab does not exist yet (cap prompt open).
  const pendingCitation = useRef<{ c: SimpleCitation; via: CitationVia; path: string } | null>(null);

  // The outer path as last seen OR last written by us; `selfNav` holds EVERY
  // outer navigation we issued and have not yet seen come back, oldest first.
  //
  // It was a single slot, which silently lost a write whenever two landed in
  // one tick — a page that consumes two deep-link params rewrites its URL twice
  // in the same commit (`citeAnchors` + `sbaChunkIds`), and `onTabLocation`
  // forwards each. The second overwrote the first, the first then arrived
  // unrecognised, and the shell "restored" it as a SECOND TAB. That tab takes
  // focus carrying the already-stripped URL, so it has no chunk to scroll to,
  // while the real tab is left hidden — where every page frame has zero height
  // and the citation scroll times out. Measured on staging 2026-10-05: one
  // citation click, two KEYTRUDA tabs, no scroll, no highlight.
  const outerRef = useRef(outerPath);
  outerRef.current = outerPath;
  const selfNav = useRef<string[]>([]);

  const syncOuter = useCallback((rawPath: string, replace: boolean) => {
    // Compared against the address bar as the browser reads it back.
    const path = normalizeShellPath(rawPath);
    if (path === outerRef.current) return;
    selfNav.current.push(path);
    outerRef.current = path;
    navigate(path, { replace });
  }, [navigate]);

  const activePath = (s: TabsState): string =>
    s.tabs.find((t) => t.id === s.activeTabId)?.path ?? '/';

  /** Every change to the tab set/focus goes through here: state, cleanup, address bar. */
  const commit = useCallback((next: TabsState) => {
    const prev = stateRef.current;
    stateRef.current = next;
    setState(next);
    const gone = prev.tabs.filter((t) => !next.tabs.some((n) => n.id === t.id)).map((t) => t.id);
    if (gone.length) {
      gone.forEach((id) => {
        delete handlersRef.current[id];
        delete queuedCitations.current[id];
      });
      const drop = <T,>(m: Record<string, T>): Record<string, T> => {
        const c = { ...m };
        gone.forEach((id) => { delete c[id]; });
        return c;
      };
      setRuns(drop);
      setContexts(drop);
    }
    syncOuter(activePath(next), true);
  }, [syncOuter]);

  const applyOpen = useCallback((path: string, opts?: { background?: boolean }) => {
    const r = requestOpen(stateRef.current, normalizeShellPath(path), Date.now(), opts);
    if (r.kind === 'needsEviction') setPending(r.pending);
    else commit(r.state);
  }, [commit]);

  const openTab = useCallback((path: string, opts?: { background?: boolean }) => {
    if (isShellPath(path)) applyOpen(path, opts);
  }, [applyOpen]);

  const closeTab = useCallback((id: string) => commit(closeTabState(stateRef.current, id)), [commit]);
  const focusTab = useCallback(
    (id: string) => commit(focusTabState(stateRef.current, id, Date.now())),
    [commit],
  );
  const reorderTab = useCallback(
    (sourceId: string, targetId: string, position: 'before' | 'after') => {
      commit(moveTabState(stateRef.current, sourceId, targetId, position));
    },
    [commit],
  );

  const setTabTitle = useCallback((id: string, title: string) => {
    const next = retitleTab(stateRef.current, id, title);
    if (next === stateRef.current) return;
    stateRef.current = next;
    setState(next);
  }, []);

  const confirmPending = useCallback(() => {
    if (!pending) return;
    setPending(null);
    const next = confirmOpen(stateRef.current, pending, Date.now());
    commit(next);
    const waiting = pendingCitation.current;
    pendingCitation.current = null;
    const opened = waiting && next.tabs.find((t) => t.path === waiting.path);
    if (waiting && opened) {
      queuedCitations.current[opened.id] = {
        c: waiting.c, via: waiting.via, pathname: pathnameOf(waiting.path),
      };
    }
  }, [pending, commit]);

  const cancelPending = useCallback(() => {
    setPending(null);
    pendingCitation.current = null;
    // A cold deep link or outer push already moved the address bar; put it back.
    syncOuter(activePath(stateRef.current), true);
  }, [syncOuter]);

  const registerTabNavigator = useCallback((tabId: string, nav: Navigator) => {
    navigators.current.set(tabId, nav);
    return () => {
      if (navigators.current.get(tabId) === nav) navigators.current.delete(tabId);
    };
  }, []);

  const onTabLocation = useCallback(
    (tabId: string, rawPath: string, type: 'PUSH' | 'POP' | 'REPLACE') => {
      const s = stateRef.current;
      const path = isShellPath(rawPath) ? normalizeShellPath(rawPath) : rawPath;
      const tab = s.tabs.find((t) => t.id === tabId);
      if (!tab) return;
      const isActive = s.activeTabId === tabId;
      if (!isShellPath(path)) {
        // Escape (e.g. /admin): put the tab back, then leave the shell. (The revert
        // returns early below at path === tab.path, so the order is not load-bearing.)
        navigators.current.get(tabId)?.(tab.path, { replace: true });
        if (isActive) navigate(path);
        return;
      }
      if (path === tab.path) return;
      const next = navigateTab(s, tabId, path);
      stateRef.current = next;
      setState(next);
      // A queued citation belongs to the page it was queued for.
      const queued = queuedCitations.current[tabId];
      if (queued && queued.pathname !== pathnameOf(path)) delete queuedCitations.current[tabId];
      if (isActive) syncOuter(path, type !== 'PUSH');
    },
    [navigate, syncOuter],
  );

  // An outer change we did not cause: Back/Forward, JobToasts, the profile menu.
  const lastKey = useRef(location.key);
  useEffect(() => {
    if (location.key === lastKey.current) return;
    lastKey.current = location.key;
    // Ours if it is anywhere in the queue, not only at the end: writes come
    // back one at a time, so an earlier one arriving while a later one is
    // still in flight is still ours. Drop it and everything before it.
    const mine = selfNav.current.indexOf(outerPath);
    if (mine !== -1) {
      selfNav.current.splice(0, mine + 1);
      return;
    }
    // A path we never wrote: the queue is stale, whatever is in it.
    selfNav.current.length = 0;
    if (!isShellPath(outerPath)) return;
    const s = stateRef.current;
    const active = s.tabs.find((t) => t.id === s.activeTabId);
    if (navType === 'POP' && active) {
      const nav = navigators.current.get(active.id);
      if (nav) nav(outerPath, { replace: true });
      else {
        const next = navigateTab(s, active.id, outerPath);
        stateRef.current = next;
        setState(next);
      }
      return;
    }
    applyOpen(outerPath);
  }, [location.key, outerPath, navType, applyOpen]);

  // Mount: bring the address bar to the active tab (rule 6: none -> `/`).
  useEffect(() => {
    if (init.current?.pending) return;
    syncOuter(activePath(stateRef.current), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeShellTabs(scopeRef.current ?? null, state);
  }, [state]);

  const setPageContext = useCallback((tabId: string, ctx: PublishedContext | null) => {
    setContexts((m) => {
      if (ctx === null) {
        if (!(tabId in m)) return m;
        const { [tabId]: _gone, ...rest } = m;
        return rest;
      }
      return { ...m, [tabId]: ctx };
    });
  }, []);

  const setPageHandlers = useCallback((
    tabId: string, h: PublishedHandlers | undefined, onlyIfCurrent?: PublishedHandlers,
  ) => {
    if (h) {
      handlersRef.current[tabId] = h;
      // Deliver a queued citation exactly once, and only to the page it was for.
      const queued = queuedCitations.current[tabId];
      const tab = stateRef.current.tabs.find((t) => t.id === tabId);
      const deliver = queued ? citationHandler(h, queued.c, queued.via) : null;
      if (queued && deliver) {
        delete queuedCitations.current[tabId];
        if (tab && pathnameOf(tab.path) === queued.pathname) deliver();
      }
    }
    // A clear from an unmounting page must not wipe its successor's handlers.
    else if (onlyIfCurrent === undefined || handlersRef.current[tabId] === onlyIfCurrent) {
      delete handlersRef.current[tabId];
    }
  }, []);

  const activeTabIdRef = useRef(state.activeTabId);
  activeTabIdRef.current = state.activeTabId;
  const activeHandlers = useCallback(
    () => (activeTabIdRef.current ? handlersRef.current[activeTabIdRef.current] : undefined),
    [],
  );
  /**
   * One entry point for every chat citation click. The destination tab is
   * resolved FIRST (the citation's target path; a null target or one on the
   * active tab's pathname stays on the active tab), then only that tab's
   * handler runs. A destination that was not the active tab (just opened or
   * just focused) gets the citation queued until its page next publishes; one
   * that never publishes relies on the URL alone.
   */
  const openCitation = useCallback((c: SimpleCitation, via: CitationVia = 'citation') => {
    // Competitive-intel sources are external pages: the shell opens them
    // itself so every tab behaves the same, handler or not.
    if (c.document_type === 'competitive_intel') {
      const safeUrl = safeCompetitiveIntelUrl(c.external_url);
      if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const rawTarget = citationTargetPath(c);
    const target = rawTarget && isShellPath(rawTarget) ? normalizeShellPath(rawTarget) : rawTarget;
    const s = stateRef.current;
    const active = s.tabs.find((t) => t.id === s.activeTabId);
    let destId: string | null = active?.id ?? null;
    let queueAs: string | null = null;
    // A same-drug tab showing another document than a linked FDA-letter
    // citation: the page picks its document from its URL (no handler switches
    // to a letter), so the tab moves there, the chunk riding along as the
    // page's own deep link. One commit, so the address bar is written once.
    const letterUrl = (tabPath: string): string | null => (
      target && c.document_type === 'fda_letter' && c.drug_id != null && c.letter_id != null
        && pathnameOf(tabPath) === pathnameOf(target) && !tabShowsFdaLetter(tabPath, c.letter_id)
        ? (c.chunk_id != null ? `${target}&fdaLetterChunkId=${c.chunk_id}` : target)
        : null);
    const moveTab = (next: TabsState, id: string, url: string) => {
      commit(navigateTab(next, id, url));
      navigators.current.get(id)?.(url);
    };
    if (target && isShellPath(target)
      && !(active && (pathnameOf(active.path) === pathnameOf(target) || citationOnTabPage(c, active.path)))) {
      const existing = s.tabs.find((t) => pathnameOf(t.path) === pathnameOf(target));
      if (existing) {
        const focused = focusTabState(s, existing.id, Date.now());
        const url = letterUrl(existing.path);
        if (url) {
          moveTab(focused, existing.id, url);
          return;
        }
        commit(focused);
        destId = existing.id;
      } else {
        const r = requestOpen(s, target, Date.now());
        if (r.kind === 'needsEviction') {
          pendingCitation.current = { c, via, path: target };
          setPending(r.pending);
          return;
        }
        commit(r.state);
        destId = r.state.activeTabId;
      }
      queueAs = destId;
    }
    if (!destId) return;
    const activeUrl = !queueAs && active ? letterUrl(active.path) : null;
    if (activeUrl) {
      moveTab(s, destId, activeUrl);
      return;
    }
    // A tab that is not active yet is queued even when it already has a
    // handler: its handler must run AFTER it re-renders as active (highlight
    // registration is gated on that), i.e. on its next publish.
    if (queueAs && target) {
      queuedCitations.current[queueAs] = { c, via, pathname: pathnameOf(target) };
      return;
    }
    citationHandler(handlersRef.current[destId], c, via)?.();
  }, [commit]);

  const reportRun = useCallback((tabId: string, run: ReportedRun) => {
    if (!stateRef.current.tabs.some((t) => t.id === tabId)) return;
    setRuns((m) => ({ ...m, [tabId]: { ...run, seq: nextRunSeq() } as RunResult }));
  }, []);

  const setShowRelevanceScore = useCallback((v: boolean) => {
    setShowRelevanceScoreState(v);
    try {
      localStorage.setItem(RELEVANCE_SCORE_KEY, String(v));
    } catch {
      /* best effort */
    }
  }, []);

  const openTabRef = useRef(openTab);
  openTabRef.current = openTab;
  useEffect(() => {
    setShellOpener((path) => {
      if (!isShellPath(path)) return false;
      openTabRef.current(path);
      return true;
    });
    return () => setShellOpener(null);
  }, []);

  const announceTab = useCallback(
    (path: string, body: string) => setAnnouncement({ path: normalizeShellPath(path), body }),
    [],
  );
  const dismissAnnouncement = useCallback(() => setAnnouncement(null), []);

  // R3.5: clear when the announced tab becomes active or is closed, or when its
  // CapPrompt is cancelled. It survives while a CapPrompt for that path is open.
  useEffect(() => {
    if (!announcement) return;
    const tab = state.tabs.find((t) => t.path === announcement.path);
    if (tab ? tab.id === state.activeTabId : pending?.path !== announcement.path) setAnnouncement(null);
  }, [announcement, state, pending]);

  const api = useMemo<ShellApi>(() => ({
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    openTab, closeTab, focusTab, moveTab: reorderTab, setTabTitle,
    setPageContext, setPageHandlers, onTabLocation,
    activeContext: state.activeTabId ? contexts[state.activeTabId] ?? null : null,
    activeHandlers,
    pending, confirmPending, cancelPending,
    registerTabNavigator,
    reportRun, runs,
    openCitation,
    showRelevanceScore, setShowRelevanceScore,
    announcement, announceTab, dismissAnnouncement,
  }), [
    state, contexts, runs, pending, showRelevanceScore, announcement, announceTab, dismissAnnouncement,
    openTab, closeTab, focusTab, reorderTab, setTabTitle, setPageContext, setPageHandlers, onTabLocation,
    activeHandlers, confirmPending, cancelPending, registerTabNavigator, reportRun,
    openCitation, setShowRelevanceScore,
  ]);

  return <ShellContext.Provider value={api}>{children}</ShellContext.Provider>;
};

export default ShellProvider;

export { useShell } from './shellContext';
